import { ApolloError } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { IGroup } from '@fluentui/react';
import React from 'react';
import {
  Spinner,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  makeStyles,
  Button,
} from '@fluentui/react-components';
import {
  CheckmarkCircleColor,
  ChevronDownRegular,
  ChevronRightRegular,
  CircleRegular,
} from '@fluentui/react-icons';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';

import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import { IRequiredItem } from './types';

interface IRequiredFieldsPanel {
  closePanel: () => void;
  doesItemHaveValue(itemKey: string): boolean;
  items: IRequiredItem[];
  groups: IGroup[];
  loading: boolean;
  error: ApolloError;
}
const useStyles = makeStyles({
  groupRow: { fontWeight: 600 },
  groupCell: { paddingTop: '10px', paddingBottom: '10px' },
  groupHeader: { display: 'flex', alignItems: 'center', gap: '8px' },
  marginLeft: { marginLeft: '15px' },
});
const RequiredFieldsPanel = ({
  closePanel,
  doesItemHaveValue,
  items,
  groups,
  loading,
  error,
}: IRequiredFieldsPanel): JSX.Element => {
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});

  const toggleGroup = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const styles = useStyles();

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Required Fields"
      isOpen
      onDismiss={closePanel}
      hasCloseButton
      closeButtonAriaLabel="Close"
      styles={CoherencePanelStyles}
    >
      <LoadingErrorMessage error={error} loading={loading} />
      {loading && <Spinner aria-label="Loading" />}
      {!loading && (
        <Table aria-label="Required fields table">
          <TableHeader>
            <TableRow className={styles.groupRow}>
              <TableHeaderCell>Field Name</TableHeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {groups.map((group) => {
              const groupItems = items.slice(group.startIndex, group.startIndex + group.count);
              const isCollapsed = !!collapsed[group.key];
              return (
                <React.Fragment key={group.key}>
                  <TableRow className={styles.groupRow}>
                    <TableCell className={styles.groupCell}>
                      <div className={styles.groupHeader}>
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={isCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
                          onClick={() => toggleGroup(group.key)}
                          aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${group.name}`}
                        />
                        <TableCellLayout>
                          {group.name} ({group.count})
                        </TableCellLayout>
                      </div>
                    </TableCell>
                  </TableRow>

                  {!isCollapsed &&
                    groupItems.map((item) => {
                      const itemHasValue: boolean = doesItemHaveValue(item.key);
                      return (
                        <TableRow key={item.key}>
                          <TableCell>
                            <TableCellLayout>
                              <div className={`${styles.groupHeader} ${styles.marginLeft}`}>
                                {itemHasValue ? <CheckmarkCircleColor /> : <CircleRegular />}
                                {item.value}
                              </div>
                            </TableCellLayout>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </CoherencePanel>
  );
};

export default RequiredFieldsPanel;
